import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { useAuth } from '../../contexts/AuthContext';

const Sites = () => {
  const { user, setCurrentSiteId } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sites, setSites] = useState([]);

  useEffect(() => {
    if (!user?.organisationId) {
      navigate('/login');
      return;
    }

    const fetchSites = async () => {
      setLoading(true);
      setError('');
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
        const res = await fetch(
          `${baseUrl}/api/sites?organisationId=${encodeURIComponent(user.organisationId)}`
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || 'Failed to load sites');
        }

        const body = await res.json();
        const s = Array.isArray(body.sites) ? body.sites : [];

        if (s.length === 0) {
          // No sites yet: go straight into onboarding flow
          navigate('/onboarding', { replace: true });
          return;
        }

        setSites(s);
      } catch (err) {
        setError(err.message || 'Error loading sites');
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, [user, navigate]);

  const handleEnterSite = (siteId) => {
    setCurrentSiteId(siteId);
    navigate('/dashboard');
  };

  const handleCreateSite = () => {
    navigate('/onboarding');
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          Your Sites
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddBusinessIcon />}
          onClick={handleCreateSite}
        >
          Create new site
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 3 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3}>
        {sites.map((site) => (
          <Grid item xs={12} sm={6} md={4} key={site._id}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
                  <StorefrontIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    {site.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {site.city || site.country || 'No location set'}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button size="small" onClick={() => handleEnterSite(site._id)}>
                  Enter site
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Sites;

